import { ICommonList } from '@/interfaces/common.interface';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface FilterButtonsProps {
  filters?: ICommonList[];
  onChange?: (filter: string) => void;
}

const FilterButtons: React.FC<FilterButtonsProps> = ({
  filters = [],
  onChange,
}) => {
  const [activeFilter, setActiveFilter] = useState<string>(
    filters[0]?.value || '',
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handleClick = (filter: string) => {
    setActiveFilter(filter);
    onChange?.(filter);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;

    const scrollAmount = 150;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const updateScrollButtons = () => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [filters]);

  return (
    <div className="flex items-center gap-2 w-full max-w-xl">
      {/* Left Scroll Button */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll('left')}
          className="shrink-0 p-2 rounded-full border text-gray-500 hover:text-primary hover:border-primary"
        >
          <ChevronLeft size={18} />
        </button>
      )}

      {/* Scrollable Filters */}
      <div
        ref={scrollRef}
        onScroll={updateScrollButtons}
        style={{ scrollbarWidth: 'thin' }}
        className="flex gap-1 sm:gap-3 overflow-x-auto w-full"
      >
        {filters.map((filter) => (
          <button
            type="button"
            key={filter.value}
            onClick={() => handleClick(filter.value)}
            className={`min-w-fit whitespace-nowrap py-2 sm:py-3 px-3 rounded-lg sm:rounded-2xl border text-sm sm:text-base
              ${
                activeFilter === filter.value
                  ? 'text-primary border-primary'
                  : 'text-gray-500 border-transparent'
              }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Right Scroll Button */}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll('right')}
          className="shrink-0 p-2 rounded-full border text-gray-500 hover:text-primary hover:border-primary"
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
};

export default FilterButtons;
